// Selection/open behavior on top of Fluent's flat tree - see docs/architecture/file-card-tree-selection.md
import type { TreeItemValue, TreeSelectionValue } from '@fluentui/react-components'
import { useHeadlessFlatTree_unstable } from '@fluentui/react-components'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { WORKING_DOCUMENT_ID } from '../../constants/files'
import type { SelectedSection } from '../../contexts/SelectedFilesContext'
import { useGetFileStructure } from '../../hooks/useGetFileStructure'
import type { DocumentSectionTree } from '../../types'
import { filterSectionTree, type HierarchyViewMode } from '../../utils/filter-section-tree'
import type { FlattenedNode, FlatTreeListItem } from './tree-selection.utils'
import {
  buildInitialCheckedItemsForView,
  buildTreeMetadataWithRoot,
  checkedItemsMapFromSelectedTrueIds,
  collectVisibleNodeIds,
  computeSelectedSectionsFromChecked,
  flattenTreeStructure,
  getSelectedTrueIdsFromSections,
  resolveSelectedTrueIds,
  selectedTrueIdsFromCheckedItems,
} from './tree-selection.utils'

const SELECTION_MODE = 'multiselect'

interface UseFileCardTreeParams {
  fileId: string
  fileName: string
  isExpanded: boolean
  onExpandToggle: () => void
  initialSelectedSections: SelectedSection[] | undefined
  onContextChange: ((sections: SelectedSection[] | null) => void) | undefined
  sections: DocumentSectionTree[] | undefined
  disabled: boolean
  hierarchyViewMode: HierarchyViewMode
}

export function useFileCardTree({
  fileId,
  fileName,
  isExpanded,
  onExpandToggle,
  initialSelectedSections,
  onContextChange,
  sections,
  disabled,
  hierarchyViewMode,
}: UseFileCardTreeParams) {
  const isWorkingDocument = fileId === WORKING_DOCUMENT_ID
  const [shouldFetchSections, setShouldFetchSections] = useState(false)
  const {
    data: fileSections,
    isLoading,
    isError,
  } = useGetFileStructure(fileId, shouldFetchSections && !isWorkingDocument && !disabled)

  const [items, setItems] = useState<FlatTreeListItem[]>([{ value: fileId, title: fileName }])

  // Controlled checkedItems for useHeadlessFlatTree_unstable.
  // Eagerly initializes whole-file selection so root aria-checked is correct on first render,
  // then resolves full tree-based selection once metadata/sections are available.
  const [checkedItems, setCheckedItems] = useState<Iterable<[TreeItemValue, TreeSelectionValue]>>(
    () => (initialSelectedSections?.length === 0 ? [[fileId, true]] : [])
  )
  const treeMetadata = useRef<Map<string, FlattenedNode>>(new Map())
  // Nodes currently shown for the active hierarchy filter (full tree metadata still holds the rest).
  const visibleIdsRef = useRef<Set<string>>(new Set([fileId]))
  // Source of truth for selection across filters - includes nodes hidden by the current view.
  const selectedTrueIdsRef = useRef<Set<string>>(new Set())
  // One-shot gate to apply ticket 4046 behavior only for the next root-open action.
  const shouldExpandMixedOnOpenRef = useRef(false)

  const checkedMap = useMemo(() => new Map(checkedItems), [checkedItems])

  const syncDisplayCheckedItems = useCallback(
    (selectedSections: SelectedSection[] | undefined) => {
      const meta = treeMetadata.current
      const visibleIds = visibleIdsRef.current

      // File removed from context - clear regardless of whether metadata is ready
      if (selectedSections == null) {
        selectedTrueIdsRef.current = new Set()
        setCheckedItems([])
        return
      }

      if (meta.size === 0) {
        // Whole-file selected but sections not loaded yet: mark root so aria-checked reflects immediately.
        // Partial selection without metadata: the tree-data effect handles it once sections load.
        if (selectedSections.length === 0) {
          setCheckedItems([[fileId, true]])
        }
        return
      }

      selectedTrueIdsRef.current = getSelectedTrueIdsFromSections(selectedSections, meta, fileId)
      setCheckedItems(buildInitialCheckedItemsForView(selectedSections, meta, visibleIds, fileId))
    },
    [fileId]
  )

  const applyFullTreeStructure = useCallback(
    (fullTreeData: DocumentSectionTree[]) => {
      const meta = buildTreeMetadataWithRoot(fullTreeData, fileId, fileName)
      treeMetadata.current = meta

      const filteredTree = filterSectionTree(fullTreeData, hierarchyViewMode)
      visibleIdsRef.current = collectVisibleNodeIds(filteredTree, fileId)
      setItems([{ value: fileId, title: fileName }, ...flattenTreeStructure(filteredTree, fileId)])
    },
    [fileId, fileName, hierarchyViewMode]
  )

  const getMixedBranchValues = useCallback((): TreeItemValue[] => {
    const mixedBranchSet = new Set<TreeItemValue>()
    checkedMap.forEach((value, key) => {
      if (value !== 'mixed') return
      const node = treeMetadata.current.get(String(key))
      // Only branches can be opened; leaves are irrelevant for openItems.
      if (!node || node.children.length === 0) return
      mixedBranchSet.add(key)
    })
    return [...mixedBranchSet]
  }, [checkedMap])

  // useHeadlessFlatTree_unstable requires a controlled openItems set to track
  // which tree nodes are expanded. This covers both the root (synced from the
  // parent's isExpanded prop) and nested branches (managed internally by the
  // onOpenChange handler which enforces one-sibling-open-at-a-time).
  const [openItems, setOpenItems] = useState<Set<TreeItemValue>>(() => new Set())

  useEffect(() => {
    setOpenItems((prev) => {
      const hasRoot = prev.has(fileId)
      if (isExpanded && !hasRoot) {
        const next = new Set(prev)
        next.add(fileId)
        if (shouldExpandMixedOnOpenRef.current) {
          getMixedBranchValues().forEach((value) => next.add(value))
        }
        return next
      }
      if (!isExpanded && prev.size > 0) {
        return new Set()
      }
      return prev
    })
  }, [isExpanded, fileId, getMixedBranchValues])

  useEffect(() => {
    if (!isExpanded || !shouldExpandMixedOnOpenRef.current) return
    if (treeMetadata.current.size === 0) return

    // Lazy-load path: metadata may become available after the initial expand click.
    // Re-apply mixed-branch expansion once tree metadata/items have been populated.
    setOpenItems((prev) => {
      const next = new Set(prev)
      next.add(fileId)
      getMixedBranchValues().forEach((value) => next.add(value))
      return next
    })

    shouldExpandMixedOnOpenRef.current = false
  }, [isExpanded, fileId, getMixedBranchValues, items.length])

  const flatTree = useHeadlessFlatTree_unstable(items, {
    selectionMode: SELECTION_MODE,
    checkedItems,
    openItems,
    onOpenChange: (_event, data) => {
      setOpenItems((prev) => {
        const eventData = data as { value?: TreeItemValue; open?: boolean }
        const changedValue = eventData.value

        const newlyOpened =
          eventData.open === true && changedValue !== undefined
            ? changedValue
            : [...data.openItems].find((item) => !prev.has(item))

        if (!newlyOpened) {
          // Item was closed - use Fluent's result as-is
          return data.openItems
        }

        const meta = treeMetadata.current.get(String(newlyOpened))
        if (!meta?.parentValue) {
          return data.openItems
        }

        const parentMeta = treeMetadata.current.get(meta.parentValue)
        if (!parentMeta) {
          return data.openItems
        }

        // Close sibling branches and their descendants (one level open at a time)
        const toClose = new Set<TreeItemValue>()
        for (const sibling of parentMeta.children) {
          if (sibling !== String(newlyOpened)) {
            toClose.add(sibling)
            treeMetadata.current.get(sibling)?.allDescendants.forEach((d) => toClose.add(d))
          }
        }

        if (toClose.size === 0) {
          return data.openItems
        }

        return new Set([...data.openItems].filter((item) => !toClose.has(item)))
      })
    },
    onCheckedChange: (_, data) => {
      // Fluent only reports checks for the visible (filtered) tree. Merge those with
      // selections that belong to nodes hidden by the current hierarchy view, then
      // drop parent IDs that Fluent marked selected only because all *visible* children
      // were checked (see resolveSelectedTrueIds).
      const visibleIds = visibleIdsRef.current

      // Fluent cycles mixed → true, but clicking an indeterminate parent should deselect
      // all its visible children instead. Detect nodes that made this transition and strip
      // them + their visible descendants from the incoming selection.
      const idsToDeselect = new Set<string>()
      data.checkedItems.forEach((value, key) => {
        if (value === true && checkedMap.get(key) === 'mixed') {
          const id = String(key)
          idsToDeselect.add(id)
          treeMetadata.current.get(id)?.allDescendants.forEach((d) => {
            if (visibleIds.has(d)) idsToDeselect.add(d)
          })
        }
      })

      const visibleTrueIds = selectedTrueIdsFromCheckedItems(data.checkedItems)
      idsToDeselect.forEach((id) => visibleTrueIds.delete(id))
      const hiddenTrueIds = [...selectedTrueIdsRef.current].filter((id) => !visibleIds.has(id))
      const mergedTrueIds = new Set([...hiddenTrueIds, ...visibleTrueIds])
      const resolvedTrueIds = resolveSelectedTrueIds(mergedTrueIds, treeMetadata.current)

      selectedTrueIdsRef.current = resolvedTrueIds

      const displayChecked = checkedItemsMapFromSelectedTrueIds(
        resolvedTrueIds,
        treeMetadata.current,
        visibleIds
      )
      setCheckedItems(displayChecked)

      if (onContextChange) {
        const contextChecked = new Map<TreeItemValue, TreeSelectionValue>()
        resolvedTrueIds.forEach((id) => contextChecked.set(id, true))
        onContextChange(
          computeSelectedSectionsFromChecked(contextChecked, treeMetadata.current, fileId)
        )
      }
    },
  })

  const cardRef = useRef<HTMLDivElement>(null)
  const observerInitialized = useRef(false)

  // Lazy load sections using Intersection Observer - only runs once
  useEffect(() => {
    if (disabled || isWorkingDocument || observerInitialized.current) {
      return
    }

    let timeoutId: ReturnType<typeof setTimeout> | undefined

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          observerInitialized.current = true
          // Small random delay to spread out requests
          timeoutId = setTimeout(() => setShouldFetchSections(true), Math.random() * 200)
          observer.disconnect()
        }
      },
      // start loading just before the card comes into view
      { rootMargin: '64px', threshold: 0 }
    )

    if (cardRef.current) observer.observe(cardRef.current)
    return () => {
      observer.disconnect()
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId)
      }
    }
  }, [disabled, isWorkingDocument, shouldFetchSections])

  /**
   * Build full-tree metadata and filtered items once tree data is available, then sync
   * selection into the visible view. Re-runs when sections/fileSections change (including
   * the working document, whose sections arrive as a prop and may refresh).
   */
  useEffect(() => {
    // Wait for data to resolve (undefined sections on working document = still loading)
    const fullTreeData = isWorkingDocument ? sections : disabled ? undefined : fileSections
    if (fullTreeData == null) return

    applyFullTreeStructure(fullTreeData)
    // For whole-file selections, checkedItems already has an eager root check from useState.
    syncDisplayCheckedItems(initialSelectedSections)
  }, [
    applyFullTreeStructure,
    disabled,
    fileSections,
    initialSelectedSections,
    isWorkingDocument,
    sections,
    syncDisplayCheckedItems,
  ])

  // Respond to hierarchy filter changes and external selection changes (e.g. Select All / Clear All)
  useEffect(() => {
    syncDisplayCheckedItems(initialSelectedSections)
  }, [hierarchyViewMode, initialSelectedSections, syncDisplayCheckedItems])

  async function handleLoadSections() {
    if (disabled) {
      return
    }

    // For lazily-loaded files, checkedItems may not be metadata-backed yet at click time;
    // use initialSelectedSections as a fallback signal for partial selection.
    const hasInitialPartialSelection = (initialSelectedSections?.length ?? 0) > 0
    const shouldExpandMixedOnOpen =
      !isExpanded && (checkedMap.get(fileId) === 'mixed' || hasInitialPartialSelection)
    shouldExpandMixedOnOpenRef.current = shouldExpandMixedOnOpen

    onExpandToggle()

    if (items.length <= 1) {
      // Ensure query is triggered if user clicks before the timer
      if (!shouldFetchSections && !isWorkingDocument) {
        setShouldFetchSections(true)
      }

      // If data is already available or it's a doc with sections, populate immediately
      const fullTreeData = isWorkingDocument && sections ? sections : fileSections
      if (fullTreeData) {
        try {
          applyFullTreeStructure(fullTreeData)
        } catch (error) {
          console.error('Failed to load file sections:', error)
        }
      }
    }
  }

  return {
    flatTree,
    items,
    checkedMap,
    treeMetadata,
    handleLoadSections,
    cardRef,
    isLoading,
    isError,
  }
}
